import { Link, useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import Button from "../global/Buttons/Button";
import { db } from "../../db";
import Context from "../../context";
import { ReactComponent as ViewMoreIcon } from '../HotelInfo/vamoos-more.svg';
import Features from "../HotelInfo/Features";
import Container from "../global/Container";
import Slider from "../global/Slider";

const SUPPORTED_DOC_MIME_TYPES = ['application/pdf', 'text/html', 'application/zip' ];
const PoiCarouselSlide = ({ slideData, numSlides, setEmptyContainerHeight, slideId, slideNum }) => {
  const { viewMode, setMobileViewGeneric, icons, t, itin } = useContext(Context);
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  const [documentViewerDocId, setDocumentViewerDocId] = useState(null);
  const [imgLink, setImgLink] = useState(null);
  const [externalLink, setExternalLink] = useState(null);
  const [navigationLink, setNavigationLink] = useState(null);
  const [storedLocationData, setStoredLocationData] = useState(null);
  const [featuresScreenActive, setFeaturesScreenActive] = useState(false);
  const navigationLinkStart = 'https://www.google.com/maps/dir';
  const numberOfWaypoints = 23; //maximum allowed number of waypoints in Google Directions API in Standard free plan
  const emptyRef = useRef(null);
  const empty = document.querySelector('.empty');

  useEffect(() => setFeaturesScreenActive(pathname.endsWith(`/maps/${slideData.id}/features`)), [slideData.id, pathname]);

  useEffect(() => {
    setMobileViewGeneric(() => {
      if (!featuresScreenActive) return null;
      return 'light';
    });
  }, [featuresScreenActive, pathname, setMobileViewGeneric, viewMode]);

  useEffect(() => {
    if (!slideData?.documentNodeId) return;
    const doc = itin?.documents.find(doc => doc.id === slideData?.documentNodeId);

    const mimeType = doc.file?.mimeType;
    const remoteUrl = doc.remoteUrl;

    if (!mimeType && !remoteUrl) return;
    if (remoteUrl) setExternalLink(remoteUrl);

    if (SUPPORTED_DOC_MIME_TYPES.includes(mimeType)) {
      setDocumentViewerDocId(doc.id)
    } else {
      setImgLink(doc?.file?.httpsUrl)
    }
  }, [itin?.documents, slideData?.documentNodeId]);

  useEffect(() => {
    if (!pathname.endsWith(slideId)) return;
    if (!emptyRef?.current) return;


    setEmptyContainerHeight(emptyRef.current.offsetHeight)
  }, [pathname, slideId, setEmptyContainerHeight, emptyRef, empty]);

  useEffect(() => {
    if (slideData.type === "route") {
      // If number of waypoints is more than maximum allowed, remove excess items
      // include the first 22 and the last 1 waypoint (to not miss out the final coordinate).
      if (slideData.waypoints.length > numberOfWaypoints) {
        const numToRemove = slideData.waypoints.length - (numberOfWaypoints - 1);
        slideData.waypoints.splice((numberOfWaypoints - 1), numToRemove, slideData.waypoints[slideData.waypoints.length - 1]);
      }
      const waypoints = slideData.waypoints.map(wp => `/${wp.latitude},${wp.longitude}`).join('');
      setNavigationLink(navigationLinkStart + waypoints)
    }
  }, [slideData.type, slideData.waypoints])

  useEffect(() => {
    if (!slideData.nesting) return;
    const getStoredItineraries = async () => {
      const storedItineraries = await db.itineraries.toArray();
      const foundItinerary = storedItineraries.find(
        storedItin =>
          storedItin.parentVamoosId !== null &&
          storedItin.operatorCode.toLowerCase() === slideData.nesting.operatorCode.toLowerCase() &&
          storedItin.referenceCode.toLowerCase() === slideData.nesting.referenceCode.toLowerCase()
      );
      setStoredLocationData({
        vamoosId: foundItinerary.vamoosId,
        brief: foundItinerary.brief,
        features: foundItinerary.features,
      })
    }
    getStoredItineraries()
  }, [slideData.id, slideData.nesting])

  const renderButton = () => {
    if (!externalLink && !documentViewerDocId && !navigationLink) return;
    return (
      <div className={!slideData.longDescription ? "placeholder" : ""}>
        {navigationLink &&
          <div className={(externalLink || documentViewerDocId) ? "contact-button-container" : ""}>
            <Button
              className="contact-button"
              outline={true}
              coloredText={true}
              onClick={() => window.open(navigationLink)}
            >
              Start navigation
            </Button>{/* TODO translation */}
          </div>
        }
        {(externalLink || documentViewerDocId) && (
          <>
            {externalLink && (
              <div className="external-link-description">
                {t('external_link_info_message', { lng: itin.language })}
              </div>
            )}
            <Button
              className="contact-button"
              onClick={() => {
                if (externalLink) {
                  window.open(externalLink);
                } else {
                  navigate(`${location.pathname}/view/${documentViewerDocId}`);
                }
              }}
            >
              {t(externalLink ? 'open_external_link' : 'open_attached_document', { lng: itin.language })}
            </Button>
          </>
        )}
      </div>
    )
  }

  const renderFeatures = () => {
    return (
      <div className="featured-features">
        {
          storedLocationData?.features
            .filter(x => x.isFeatured)
            .map((x, i) => (<img key={i} src={icons[x.iconId]} alt={`${x.groupName}: ${x.name}`} />))
        }
        <Link className="view-more-button" to={pathname.includes('/daily/') ? `${slideNum}/maps/${slideData.id}/features` : `${slideData.id}/features`}>
          <ViewMoreIcon />
        </Link>
      </div>
    );
  };

  const renderText = () => {
    const longDescription = slideData.longDescription || slideData.nesting?.longDescription;
    return (
      <div className="description">
        <div className="short-description">
          {
            slideData.nesting?.shortDescription ?? null
          }
        </div>
        <div className="feature-description">
          {
            storedLocationData?.features?.length
              ? renderFeatures()
              : slideData.nesting?.longDescription &&  slideData.nesting?.shortDescription
                ? <hr />
                : null
          }
        </div>
        <div className="long-description">
          {
            longDescription && longDescription.split(/\n/).map((line, i) => {
              const urlRegex = /(https?:\/\/\S+)/g;

              // Split the line by the detected URLs and wrap them in anchor tags
              const parts = line.split(urlRegex).map((part, index) => {
                if (urlRegex.test(part)) {
                  return <a href={part} key={index} target="_blank" rel="noreferrer">{part}</a>;
                }
                return part;
              });

              return <p key={i}>{parts}</p>;
            })
          }
        </div>
      </div>
    );
  };

  useEffect(() => {
    const slide = document.getElementById('carousel-slide-' + slideId);
    const content = slide.querySelector('.content');

    const wheelMove = e => slide.scrollTop += e.deltaY;

    let initialY = null;
    const startTouch = e => initialY = e.touches[0].clientY;
    const moveTouch = e => {
      if (initialY === null) return;
      const currentY = e.touches[0].clientY;
      const diffY = initialY - currentY;
      slide.scrollTop += (diffY);
      initialY = currentY;
      e.preventDefault();
    };

    content.addEventListener('wheel', wheelMove, { passive: true });
    content.addEventListener('touchstart', startTouch, { passive: true });
    content.addEventListener('touchmove', moveTouch);

    return () => {
      content.removeEventListener('wheel', wheelMove);
      content.removeEventListener('touchstart', startTouch);
      content.removeEventListener('touchmove', moveTouch);
    };
  }, [slideId]);

  const renderContent = () => {
    if (featuresScreenActive) return <Container style={{ headerBackGroundColor: 'header-white' }}><Features features={storedLocationData?.features} section='PoiCarouselSlide'/></Container>;

    const sliderData = storedLocationData?.brief?.map(data => ({
        headline: data?.headline,
        link: data?.imageNode?.file?.httpsUrl,
    }));
    return (
      <>
        <div className="empty" ref={emptyRef} />
        <div className="content">
          {
            storedLocationData?.brief?.length
              ? <div className="slider">
                  <Slider data={sliderData} />
              </div>
              : imgLink &&
              <div className="image">
                <img src={imgLink} alt="" />
              </div>
          }
          {renderText()}
          {renderButton()}
        </div>
      </>
    )
  }

  return (
    <div
      className={"poi-carousel-slide num-slides-" + numSlides}
      id={`carousel-slide-${slideId}`}
    >
      { renderContent() }
    </div>
  );
};

export default PoiCarouselSlide;
